import { Link } from "react-router-dom";
import { FileDown, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { useProducts, hplcPath } from "@/lib/products";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SAMPLE_ID = "abl-bpc-10";

export default function LabStandards() {
  const products = useProducts();
  const p = products.find((x) => x.id === SAMPLE_ID);
  const rows = [
    ["Batch / Lot", p?.coa_lot || "ABL-BPC-2602", true],
    ["Appearance", "White lyophilized powder", true],
    ["Chromatographic purity", p?.coa_purity ? `${p.coa_purity}%` : "99.59%", true],
    ["Identity", "LC-MS confirmed · C₆₂H₉₈N₁₆O₂₂", true],
    ["Unit mass", "11.88 mg / vial (spec 10 mg)", true],
    ["Method", "HPLC-UV 214 nm · XBridge BEH C18", true],
    ["Source laboratory", "Freedom Diagnostics", true],
  ];

  return (
    <section id="standards" className="bg-[#0B111A] text-white border-y border-slate-800" data-testid="lab-standards-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal>
          <p className="font-tech text-xs uppercase tracking-[0.18em] text-emerald-400 mb-3">Lab standards</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
            What is inside<br />the vial matters.
          </h2>
          <p className="mt-6 text-base text-slate-300 leading-relaxed max-w-lg">
            Arias Peptides lists only lots that carry a manufacturer Certificate of Analysis from an independent
            laboratory. We reproduce each certificate unaltered — purity, identity, unit mass and method — so your
            bench can audit the record before a vial ever ships.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/coa-library" data-testid="standards-cta-coa"
              className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-200 font-medium text-sm px-6 py-3 rounded-full transition-colors">
              Browse the COA Library
            </Link>
            <a href="#quality" data-testid="standards-cta-quality"
              className="inline-flex items-center gap-2 border border-slate-700 hover:border-slate-400 text-slate-200 font-medium text-sm px-6 py-3 rounded-full transition-colors">
              How lots are tested <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            {[["-20°C", "Lyophilized storage"], ["214 nm", "UV detection"], ["x2", "Methods per lot"]].map(([v, l]) => (
              <div key={l}>
                <p className="font-tech text-xl font-semibold text-white">{v}</p>
                <p className="text-xs text-slate-400 mt-1 uppercase tracking-wide">{l}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="border border-slate-800 bg-[#111827]" data-testid="lab-standards-sample-coa">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
              <div>
                <p className="font-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">COA sample · Axis Bio Lab</p>
                <p className="font-display text-xl font-semibold mt-1">BPC-157 <span className="text-slate-400 font-medium">10 mg</span></p>
              </div>
              <span className="font-tech text-[10px] uppercase tracking-[0.16em] border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 px-2 py-1">All specs met</span>
            </div>
            <div className="divide-y divide-slate-800">
              {rows.map(([k, v, ok]) => (
                <div key={k} className="grid grid-cols-[1fr_auto] gap-4 px-5 py-3 text-sm">
                  <div>
                    <p className="font-tech text-[10px] uppercase tracking-[0.16em] text-slate-500">{k}</p>
                    <p className="font-tech text-slate-100 mt-0.5">{v}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 font-tech text-[10px] uppercase tracking-wide text-emerald-400 self-center">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {ok ? "Pass" : "—"}
                  </span>
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-slate-800">
              <p className="font-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">HPLC chromatogram · 214 nm · illustrative trace</p>
              <svg viewBox="0 0 300 100" className="w-full h-20">
                <path d={hplcPath(SAMPLE_ID)} fill="none" stroke="#38bdf8" strokeWidth="1.5" />
                <line x1="0" y1="90" x2="300" y2="90" stroke="#1e293b" strokeWidth="1" />
              </svg>
            </div>
            <div className="px-5 py-4 border-t border-slate-800 flex items-center justify-between gap-3">
              <p className="text-[11px] text-slate-500">Example batch record · issued 6/18/2026</p>
              <a href={`${API}/coa/${SAMPLE_ID}`} target="_blank" rel="noopener noreferrer" data-testid="standards-sample-coa-download"
                className="inline-flex items-center gap-1.5 font-tech text-[11px] uppercase tracking-wide text-white hover:text-emerald-400 transition-colors">
                <FileDown className="h-3.5 w-3.5" /> Original PDF
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
